// Merchant coupon CRUD — server scopes everything to their owned store, so
// the form omits the store picker entirely.

import React, { useEffect, useState } from 'react';
import {
  createCoupon,
  deleteCoupon,
  listCoupons,
  updateCoupon,
} from '../../services/merchantService';
import DataTable from '../common/DataTable';
import ConfirmDialog from '../common/ConfirmDialog';
import PortalModal, { ModalActions } from '../common/PortalModal';
import Field from '../common/Field';

const DISCOUNT_TYPES = ['flat', 'percent', 'bogo', 'free_item'];
const today = () => new Date().toISOString().slice(0, 10);
const plus = (days) => new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);
const blank = {
  title: '', description: '', discount_type: 'percent', discount_value: '10',
  minimum_purchase: 0, valid_from: today(), valid_until: plus(30),
  status: 'active', max_claims: 0, image_url: '',
};

export default function MerchantCoupons() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [expiryFilter, setExpiryFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmDel, setConfirmDel] = useState(null);

  const reload = () => listCoupons().then(d => setRows(d.coupons || [])).catch(() => {});
  useEffect(() => { reload(); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const needle = q.trim().toLowerCase();
  const visible = rows.filter(c => {
    if (needle) {
      const hay = `${c.title} ${c.description || ''}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    if (statusFilter && c.status !== statusFilter) return false;
    if (typeFilter && c.discount_type !== typeFilter) return false;
    if (expiryFilter === 'active' && c.valid_until < today) return false;
    if (expiryFilter === 'expired' && c.valid_until >= today) return false;
    return true;
  });

  const resetFilters = () => { setQ(''); setStatusFilter(''); setTypeFilter(''); setExpiryFilter(''); };
  const hasFilters = q || statusFilter || typeFilter || expiryFilter;

  const openCreate = () => { setEditing(null); setForm(blank); setShowForm(true); setError(''); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      title: c.title, description: c.description,
      discount_type: c.discount_type, discount_value: c.discount_value,
      minimum_purchase: c.minimum_purchase, valid_from: c.valid_from, valid_until: c.valid_until,
      status: c.status, max_claims: c.max_claims, image_url: c.image_url || '',
    });
    setShowForm(true);
    setError('');
  };

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      if (editing) await updateCoupon(editing.id, form);
      else await createCoupon(form);
      setShowForm(false);
      reload();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    await deleteCoupon(confirmDel.id);
    setConfirmDel(null);
    reload();
  };

  return (
    <>
      <div className="portal-content-header">
        <h1>My coupons</h1>
        <button type="button" className="portal-button" onClick={openCreate}>+ New coupon</button>
      </div>

      <div className="portal-filter-bar">
        <input
          type="search"
          className="portal-search"
          placeholder="Search title or description"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        <select className="portal-search" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select className="portal-search" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All discount types</option>
          {DISCOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="portal-search" value={expiryFilter} onChange={e => setExpiryFilter(e.target.value)}>
          <option value="">All validity</option>
          <option value="active">Still valid</option>
          <option value="expired">Expired</option>
        </select>
        {hasFilters && <button type="button" className="portal-button secondary" onClick={resetFilters}>Clear</button>}
        <span className="meta">{visible.length} of {rows.length} coupons</span>
      </div>

      <DataTable
        columns={[
          { key: 'title', label: 'Title', render: c => <strong>{c.title}</strong> },
          { key: 'discount', label: 'Discount', render: c => `${c.discount_value} (${c.discount_type})` },
          { key: 'valid_from', label: 'From' },
          { key: 'valid_until', label: 'Until' },
          { key: 'status', label: 'Status', render: c => <span className={`badge badge-${c.status === 'active' ? 'active' : 'inactive'}`}>{c.status}</span> },
          { key: 'claim_count', label: 'Claims' },
          { key: 'actions', label: '', render: c => (
            <div className="row-actions">
              <button type="button" className="portal-button secondary" onClick={() => openEdit(c)}>Edit</button>
              <button type="button" className="portal-button danger" onClick={() => setConfirmDel(c)}>Delete</button>
            </div>
          ) },
        ]}
        rows={visible}
        emptyMessage={hasFilters ? 'No coupons match the current filters.' : 'No coupons yet — create one to start attracting customers.'}
      />

      <PortalModal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? `Edit: ${editing.title}` : 'New coupon'}
      >
        {error && <div className="portal-error">{error}</div>}
        <div className="portal-form-grid">
          <Field label="Title">
            <input className="portal-search" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
          </Field>
          <Field label="Discount type">
            <select className="portal-search" value={form.discount_type} onChange={e => setForm({ ...form, discount_type: e.target.value })}>
              {DISCOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Discount value">
            <input className="portal-search" value={form.discount_value} onChange={e => setForm({ ...form, discount_value: e.target.value })} />
          </Field>
          <Field label="Minimum purchase ($)">
            <input className="portal-search" value={form.minimum_purchase} onChange={e => setForm({ ...form, minimum_purchase: e.target.value })} />
          </Field>
          <Field label="Valid from">
            <input type="date" className="portal-search" value={form.valid_from} onChange={e => setForm({ ...form, valid_from: e.target.value })} />
          </Field>
          <Field label="Valid until">
            <input type="date" className="portal-search" value={form.valid_until} onChange={e => setForm({ ...form, valid_until: e.target.value })} />
          </Field>
          <Field label="Max claims (0 = unlimited)">
            <input className="portal-search" value={form.max_claims} onChange={e => setForm({ ...form, max_claims: e.target.value })} />
          </Field>
          <Field label="Status">
            <select className="portal-search" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </Field>
          <Field label="Image URL (optional)" full>
            <input className="portal-search" value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} />
          </Field>
          <Field label="Description" full>
            <textarea className="portal-textarea" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          </Field>
        </div>
        <ModalActions>
          <button type="button" className="portal-button secondary" onClick={() => setShowForm(false)}>Cancel</button>
          <button type="button" className="portal-button" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        </ModalActions>
      </PortalModal>

      <ConfirmDialog
        open={!!confirmDel}
        title="Delete coupon?"
        message={`Delete "${confirmDel?.title}" and revoke existing claims? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={doDelete}
        onCancel={() => setConfirmDel(null)}
      />
    </>
  );
}
